import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ExportToolbar from "@/components/exporter/ExportToolbar";
import DocumentBody from "@/components/exporter/DocumentBody";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Exporter({ plan, cards, pillars, onSummaryUpdate, lockReason }) {
  const [synth, setSynth] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [docxBusy, setDocxBusy] = useState(false);
  const [pdfLink, setPdfLink] = useState(null);
  const [docxLink, setDocxLink] = useState(null);
  const [recent, setRecent] = useState([]);
  const [showRecent, setShowRecent] = useState(false);
  const [notesEnabled, setNotesEnabled] = useState(true);
  const recentRef = useRef(null);

  const planId = plan._id || plan.id;
  const loadRecent = useCallback(async () => {
    try {
      setRecent(await api.listExports(planId));
    } catch (e) {
      console.warn("Could not load recent exports", e);
    }
  }, [planId]);
  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  // Auto-dismiss the "file ready" bar after a while (it also has a manual close).
  useEffect(() => {
    if (!pdfLink && !docxLink) return;
    const t = setTimeout(() => {
      setPdfLink(null);
      setDocxLink(null);
    }, 20000);
    return () => clearTimeout(t);
  }, [pdfLink, docxLink]);

  // Invalidate any previously-generated download link the moment the plan content
  // changes (a card edit, AI refinement, or re-synthesized summary). This prevents a
  // user from downloading a stale file that no longer matches the live document.
  useEffect(() => {
    setPdfLink(null);
    setDocxLink(null);
  }, [cards, plan.executive_summary]);

  // Close the Recent dropdown on an outside click.
  useEffect(() => {
    if (!showRecent) return;
    const onDoc = (e) => {
      if (recentRef.current && !recentRef.current.contains(e.target)) setShowRecent(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showRecent]);

  // Collect the styles that drive the on-screen artifact so the server can render an
  // identical PDF. <style> blocks are sent verbatim (preserves @import order); external
  // stylesheets are fetched and inlined so this also works in the deployed build.
  const collectStyles = async () => {
    const nodes = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'));
    const parts = await Promise.all(
      nodes.map(async (el) => {
        if (el.tagName === "STYLE") return el.outerHTML;
        try {
          const css = await fetch(el.href).then((r) => r.text());
          return `<style>${css}</style>`;
        } catch {
          return el.outerHTML;
        }
      })
    );
    return parts.join("\n");
  };

  // Fetch the generated file's bytes over the normal API connection (always works) so
  // we can offer a same-page download. We ALSO keep the real file URL — a direct user
  // click on that (opened in a new tab) is the most reliable path inside the locked-down
  // preview iframe, where blob/auto downloads can be silently blocked.
  const deliver = async (downloadUrl, filename, setLink) => {
    const absoluteUrl = `${BACKEND_URL}${downloadUrl}`;
    let href = absoluteUrl;
    try {
      const res = await axios.get(absoluteUrl, { responseType: "blob" });
      href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.warn("Blob download blocked; falling back to direct URL", e);
    }
    setLink({ href, filename, url: absoluteUrl });
    loadRecent();
  };

  const downloadPdf = async () => {
    const node = document.getElementById("print-root");
    if (!node) return;
    setPdfBusy(true);
    setPdfLink(null);
    try {
      const styles = await collectStyles();
      const res = await api.exportPdfFile(planId, node.outerHTML, styles, notesEnabled);
      await deliver(res.download_url, res.filename, setPdfLink);
      toast.success("PDF ready — use the Save PDF link if it didn't download.");
    } catch (e) {
      toast.error("PDF export failed. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  const downloadDocx = async () => {
    setDocxBusy(true);
    setDocxLink(null);
    try {
      const res = await api.exportDocxFile(planId);
      await deliver(res.download_url, res.filename, setDocxLink);
      toast.success("Word document ready — use the Save DOCX link if it didn't download.");
    } catch (e) {
      toast.error("DOCX export failed. Please try again.");
    } finally {
      setDocxBusy(false);
    }
  };

  const synthesize = async () => {
    setSynth(true);
    try {
      await api.synthesize(planId);
      const start = Date.now();
      const poll = setInterval(async () => {
        const p = await api.getPlan(planId).catch(() => null);
        if (p && p.summary_status === "ready") {
          clearInterval(poll);
          onSummaryUpdate(p.executive_summary);
          setSynth(false);
          toast.success("Executive summary synthesized.");
        } else if ((p && p.summary_status === "error") || Date.now() - start > 120000) {
          clearInterval(poll);
          setSynth(false);
          toast.error("Synthesis failed. Please retry.");
        }
      }, 3000);
    } catch (e) {
      setSynth(false);
      toast.error("Synthesis failed.");
    }
  };

  const copyMarkdown = async () => {
    try {
      const res = await api.exportMarkdown(planId);
      const md = res.markdown;
      try {
        await navigator.clipboard.writeText(md);
      } catch (clipErr) {
        const ta = document.createElement("textarea");
        ta.value = md;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Markdown copied to clipboard.");
    } catch (e) {
      toast.error("Copy failed.");
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-100">
      <ExportToolbar
        plan={plan}
        lockReason={lockReason}
        synth={synth}
        pdfBusy={pdfBusy}
        docxBusy={docxBusy}
        notesEnabled={notesEnabled}
        setNotesEnabled={setNotesEnabled}
        onSynthesize={synthesize}
        onCopyMarkdown={copyMarkdown}
        onDownloadDocx={downloadDocx}
        onDownloadPdf={downloadPdf}
        showRecent={showRecent}
        setShowRecent={setShowRecent}
        recent={recent}
        loadRecent={loadRecent}
        recentRef={recentRef}
        pdfLink={pdfLink}
        docxLink={docxLink}
        onDismissReady={() => { setPdfLink(null); setDocxLink(null); }}
      />
      <DocumentBody plan={plan} cards={cards} pillars={pillars} notes={notesEnabled} />
    </div>
  );
}
